/**
 * A client's brand colour, turned into the handful of shades a theme needs.
 *
 * A client gives you one hex value — the one in their brand guide — and nothing else. Asking for a
 * lighter shade, a darker shade and a text colour that reads on top gets you either a shrug or
 * three numbers somebody picked in the moment. So the brand contract takes the one colour they
 * actually have, and the rest is derived here.
 *
 * Until this existed, a dedicated instance wore the client's logo on SharePass's blue. That is
 * fine for YMtech, whose blue is close to ours, and wrong for Schur (red) and Arcare (violet).
 */

/** #abc and #aabbcc, with or without the hash. Null for anything else, so a typo cannot ship. */
export function parseHex(value: string): { r: number; g: number; b: number } | null {
    const hex = value.trim().replace(/^#/, '');
    const full = hex.length === 3 ? [...hex].map((c) => c + c).join('') : hex;
    if (!/^[0-9a-fA-F]{6}$/.test(full)) return null;
    return {
        r: parseInt(full.slice(0, 2), 16),
        g: parseInt(full.slice(2, 4), 16),
        b: parseInt(full.slice(4, 6), 16),
    };
}

const toHex = (n: number) =>
    Math.round(Math.min(255, Math.max(0, n)))
        .toString(16)
        .padStart(2, '0');

/** `amount` of the way from one colour to another: 0 is the first, 1 the second. */
export function mix(from: string, to: string, amount: number): string {
    const a = parseHex(from);
    const b = parseHex(to);
    if (!a || !b) return from;
    const at = (x: number, y: number) => toHex(x + (y - x) * amount);
    return `#${at(a.r, b.r)}${at(a.g, b.g)}${at(a.b, b.b)}`.toUpperCase();
}

/**
 * Relative luminance, as WCAG defines it. Used for one decision only: whether text on top of this
 * colour should be white or near-black. A brand whose colour is a bright yellow gets dark text; one
 * whose colour is a deep red gets white. Guessing wrong makes a button's label unreadable.
 */
export function luminance(value: string): number {
    const rgb = parseHex(value);
    if (!rgb) return 0;
    const channel = (c: number) => {
        const s = c / 255;
        return s <= 0.03928 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
    };
    return 0.2126 * channel(rgb.r) + 0.7152 * channel(rgb.g) + 0.0722 * channel(rgb.b);
}

/** White or near-black, whichever reads on this colour. The threshold is where the two contrasts meet. */
export function readableOn(value: string): string {
    return luminance(value) > 0.45 ? '#0F172A' : '#FFFFFF';
}

export interface BrandShades {
    main: string;
    light: string;
    dark: string;
    contrastText: string;
    /** The diagonal the kit's buttons are painted with, in the brand's own hue. */
    gradient: string;
    /** The tint a row takes when the pointer is over it. */
    hover: string;
}

/**
 * The shades, for one theme mode.
 *
 * Light and dark are mixed toward white and black rather than by moving the hue: a brand colour is
 * the one thing a client will notice being altered, so the derived shades stay on it. On a dark
 * page the lighter shade leads, because the darker one sinks into the background.
 */
export function brandShades(primary: string, mode: 'light' | 'dark'): BrandShades | null {
    if (!parseHex(primary)) return null;

    const light = mix(primary, '#FFFFFF', 0.25);
    const dark = mix(primary, '#000000', 0.25);
    const rgb = parseHex(primary);
    const main = mode === 'dark' ? light : primary;

    return {
        main,
        light: mode === 'dark' ? mix(primary, '#FFFFFF', 0.45) : light,
        dark,
        contrastText: readableOn(main),
        gradient: `linear-gradient(to bottom left, ${primary}, ${dark}, ${primary})`,
        hover: rgb ? `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, ${mode === 'light' ? 0.06 : 0.12})` : 'transparent',
    };
}
